import { createContext, useContext, useMemo, type ReactNode } from 'react'

import type { CommentData } from '~publish/gql/graphql'

// ========================================
// Context Types
// ========================================

/**
 * CommentContext value - minimal context containing only core comment data
 *
 * Following the PostCardContext pattern, this context only provides the
 * essential comment data. All other props (channel, callbacks, state)
 * are passed directly to components as needed.
 */
type CommentContextValue = {
  /** The comment data */
  comment: CommentData
}

// ========================================
// Context Definition
// ========================================

const defaultValue: CommentContextValue = {
  // Default value for context - comment should never actually be null in practice
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  comment: null as unknown as CommentData,
}

export const CommentContext = createContext<CommentContextValue>(defaultValue)

// ========================================
// Provider Component
// ========================================

export type CommentProviderProps = {
  comment: CommentData
  children: ReactNode
}

/**
 * CommentContextProvider - Provides comment context to child components
 *
 * This provider allows child components to access comment data without prop drilling.
 * Only core comment data is provided via context. All other props (channel, callbacks, state)
 * are passed directly to components.
 *
 * @example
 * ```tsx
 * <CommentContextProvider comment={comment}>
 *   <CommentAvatar channel={channel} />
 *   <CommentHeader />
 *   <CommentContent />
 *   <CommentActions channel={channel} onDismiss={handleDismiss} />
 * </CommentContextProvider>
 * ```
 */
export const CommentContextProvider = ({
  comment,
  children,
}: CommentProviderProps): JSX.Element => {
  const contextValue: CommentContextValue = useMemo(
    () => ({ comment }),
    [comment],
  )

  return (
    <CommentContext.Provider value={contextValue}>
      {children}
    </CommentContext.Provider>
  )
}

CommentContextProvider.displayName = 'CommentContextProvider'

// ========================================
// Context Hook
// ========================================

/**
 * useCommentContext - Hook to access comment context
 *
 * This hook provides access to comment data within a CommentContextProvider tree.
 * It includes type safety and helpful error messaging for development.
 *
 * @throws {Error} When used outside of CommentContextProvider
 * @returns {CommentContextValue} The comment context value
 *
 * @example
 * ```tsx
 * const CommentHeader = () => {
 *   const { comment } = useCommentContext()
 *   return <span>{comment.author.username}</span>
 * }
 * ```
 */
export const useCommentContext = (): CommentContextValue => {
  const context = useContext(CommentContext)

  if (context === undefined) {
    throw new Error(
      'useCommentContext must be used within a CommentContextProvider. ' +
        'Wrap your component tree with <CommentContextProvider> to use this hook.',
    )
  }

  if (context === defaultValue) {
    throw new Error('useCommentContext must have an initialized comment value')
  }

  return context
}

// ========================================
// Utility Hook
// ========================================

/**
 * useCommentData - Convenience hook to get just the comment data
 *
 * This is a convenience hook that extracts just the comment data from the context.
 * Use this when you only need the comment data and not the full context object.
 *
 * @returns {CommentData} The comment data from context
 *
 * @example
 * ```tsx
 * const CommentContent = () => {
 *   const comment = useCommentData()
 *   return <p>{comment.content.text}</p>
 * }
 * ```
 */
export const useCommentData = (): CommentData => {
  const { comment } = useCommentContext()
  return comment
}
